import { useCallback, useMemo, useState } from 'react';

import { LoadingButton } from '@mui/lab';
import {
  FormControl,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Typography,
  Box,
} from '@mui/material';

import { removeOrganizations } from 'api';
import { useSnackContext } from 'hooks/useSnackContext';
import { OrganizationVerboseProps, OrganizationProps } from 'types';

import { CheckboxWrapper } from './styledComponents';
export type OrganizationRemoveProps = {
  organizations: Array<OrganizationProps>;
  org_id: number;
  setOrganization: React.Dispatch<
    React.SetStateAction<OrganizationVerboseProps>
  >;
};

export const OrganizationRemove: React.FC<OrganizationRemoveProps> = (
  props,
) => {
  const { organizations, org_id, setOrganization } = props;
  const [checkedArray, setCheckedArray] = useState<Array<number>>([]);
  const { setSnack } = useSnackContext();
  const [submitting, setSubmitting] = useState(false);

  const handleOnChange = useCallback(
    (checked_id: number) => {
      const updatedCheckedState = checkedArray.includes(checked_id)
        ? checkedArray.filter((id) => id !== checked_id)
        : [...checkedArray, checked_id];
      setCheckedArray(updatedCheckedState);
    },
    [checkedArray],
  );

  const handleRemoveOrgs = useCallback(async () => {
    setSubmitting(true);
    try {
      const success = await removeOrganizations({
        org_id: org_id,
        child_ids: checkedArray,
      });
      if (success) {
        const newOrgs = organizations.filter(
          (org) => !checkedArray.includes(org.id),
        );
        setCheckedArray([]);
        setOrganization((prevOrg) => ({ ...prevOrg, children: newOrgs }));
      }
    } catch (e) {
      setSnack((props) => ({
        ...props,
        severity: 'error',
        open: true,
        message: 'Failed To Remove Organization',
        error: e as Error,
      }));
    } finally {
      setSubmitting(false);
    }
  }, [checkedArray, org_id, organizations, setOrganization, setSnack]);

  const disabled = useMemo(() => {
    return checkedArray.length === 0;
  }, [checkedArray]);

  return (
    <>
      <CheckboxWrapper>
        <FormControl component='fieldset' variant='standard'>
          <FormGroup>
            {organizations.map(({ id, name }) => (
              <FormControlLabel
                key={id}
                control={
                  <Checkbox
                    checked={checkedArray.includes(id)}
                    onChange={() => handleOnChange(id)}
                    name={name}
                  />
                }
                label={<Typography variant='body1'>{name}</Typography>}
              />
            ))}
          </FormGroup>
        </FormControl>
      </CheckboxWrapper>
      <Box
        sx={{
          mt: 1,
          display: 'flex',
          flexFlow: 'row',
        }}
      >
        <LoadingButton
          sx={{ marginTop: 'auto', marginLeft: 'auto', alignSelf: 'flex-end' }}
          type='submit'
          loading={submitting}
          disabled={disabled}
          onClick={handleRemoveOrgs}
          variant='contained'
        >
          REMOVE
        </LoadingButton>
      </Box>
    </>
  );
};
